import React from 'react';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createStackNavigator } from '@react-navigation/stack';
import { useTheme } from 'react-native-paper';
import { screenOptions } from './NavigationUtils';

// Icon bileşeni
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

// Ekranlarımızı import edelim
import { 
  HomeScreen, 
  ChildrenScreen,
  AddChildScreen,
  ChildDetailScreen,
  ActivitiesScreen,
  ActivityDetailScreen,
  CreateActivityScreen,
  MenuScreen,
  MenuCategoryScreen,
  MenuItemScreen,
  ProfileScreen,
  EditProfileScreen,
  ChangePasswordScreen,
  AboutScreen,
  ParkEntryScreen,
  ParkHistoryScreen,
  ActiveParkActivitiesScreen,
  PendingActivitiesScreen
} from '../screens/user';

const Tab = createBottomTabNavigator();
const HomeStack = createStackNavigator();
const ChildrenStack = createStackNavigator();
const ActivitiesStack = createStackNavigator();
const MenuStack = createStackNavigator();
const ProfileStack = createStackNavigator();

// Ana sayfa stack navigasyonu
const HomeStackNavigator = () => (
  <HomeStack.Navigator screenOptions={screenOptions}>
    <HomeStack.Screen name="HomeMain" component={HomeScreen} options={{ title: 'İlkay Kids' }} />
    <HomeStack.Screen name="ParkEntry" component={ParkEntryScreen} options={{ title: 'Park Giriş' }} />
    <HomeStack.Screen name="ActiveParkActivities" component={ActiveParkActivitiesScreen} options={{ title: 'Aktif Park Ziyaretleri' }} />
    <HomeStack.Screen name="AddChild" component={AddChildScreen} options={{ title: 'Çocuk Ekle' }} />
  </HomeStack.Navigator>
);

// Çocuklar stack navigasyonu
const ChildrenStackNavigator = () => (
  <ChildrenStack.Navigator screenOptions={screenOptions}>
    <ChildrenStack.Screen name="ChildrenList" component={ChildrenScreen} options={{ title: 'Çocuklarım' }} />
    <ChildrenStack.Screen name="ChildDetail" component={ChildDetailScreen} options={{ title: 'Çocuk Detayı' }} />
    <ChildrenStack.Screen name="AddChild" component={AddChildScreen} options={{ title: 'Çocuk Ekle' }} />
    <ChildrenStack.Screen name="ParkEntry" component={ParkEntryScreen} options={{ title: 'Park Giriş' }} />
  </ChildrenStack.Navigator>
);

// Aktiviteler stack navigasyonu
const ActivitiesStackNavigator = () => (
  <ActivitiesStack.Navigator 
    screenOptions={screenOptions}
    initialRouteName="ActivitiesList"
  >
    <ActivitiesStack.Screen name="ActivitiesList" component={ActivitiesScreen} options={{ title: 'Ziyaretler' }} />
    <ActivitiesStack.Screen name="ActivityDetail" component={ActivityDetailScreen} options={{ title: 'Aktivite Detayı' }} />
    <ActivitiesStack.Screen name="CreateActivity" component={CreateActivityScreen} options={{ title: 'Aktivite Oluştur' }} />
    <ActivitiesStack.Screen name="ParkEntry" component={ParkEntryScreen} options={{ title: 'Park Giriş' }} />
    <ActivitiesStack.Screen name="ParkHistory" component={ParkHistoryScreen} options={{ title: 'Oyun Parkı Geçmişi' }} />
    <ActivitiesStack.Screen name="ActiveParkActivities" component={ActiveParkActivitiesScreen} options={{ title: 'Aktif Park Ziyaretleri' }} />
    <ActivitiesStack.Screen name="PendingActivities" component={PendingActivitiesScreen} options={{ title: 'Onay Bekleyen Ziyaretler' }} />
    <ActivitiesStack.Screen name="AddChild" component={AddChildScreen} options={{ title: 'Çocuk Ekle' }} />
  </ActivitiesStack.Navigator>
);

// Menü stack navigasyonu
const MenuStackNavigator = () => (
  <MenuStack.Navigator screenOptions={screenOptions}>
    <MenuStack.Screen name="MenuMain" component={MenuScreen} options={{ title: 'Menü' }} />
    <MenuStack.Screen 
      name="MenuCategory" 
      component={MenuCategoryScreen} 
      options={({ route }) => ({ 
        title: route.params?.category?.name || 'Kategori'
      })} 
    />
    <MenuStack.Screen 
      name="MenuItem" 
      component={MenuItemScreen} 
      options={({ route }) => ({ 
        title: route.params?.item?.name || 'Ürün Detayı'
      })} 
    />
  </MenuStack.Navigator>
);

// Profil stack navigasyonu
const ProfileStackNavigator = () => (
  <ProfileStack.Navigator screenOptions={screenOptions}>
    <ProfileStack.Screen 
      name="ProfileMain" 
      component={ProfileScreen} 
      options={{ 
        title: 'Profil',
      }} 
    />
    <ProfileStack.Screen 
      name="EditProfile" 
      component={EditProfileScreen} 
      options={{ 
        title: 'Profili Düzenle',
      }} 
    />
    <ProfileStack.Screen 
      name="ChangePassword" 
      component={ChangePasswordScreen} 
      options={{ 
        title: 'Şifre Değiştir',
      }} 
    />
    <ProfileStack.Screen 
      name="Children" 
      component={ChildrenScreen} 
      options={{ 
        title: 'Çocuklarım',
      }} 
    />
    <ProfileStack.Screen 
      name="About" 
      component={AboutScreen} 
      options={{ 
        title: 'Hakkında',
      }} 
    />
  </ProfileStack.Navigator>
);

// Ana tab navigasyonu
const UserNavigator = () => {
  const theme = useTheme();
  
  return (
    <Tab.Navigator
      initialRouteName="Home"
      backBehavior="initialRoute"
      screenOptions={{
        tabBarActiveTintColor: '#e8a2f9',
        tabBarInactiveTintColor: '#757575',
        tabBarStyle: { paddingBottom: 5, height: 60, backgroundColor: 'rgb(255, 255, 255)' },
        headerShown: false,
      }}
    >
      <Tab.Screen 
        name="Home" 
        component={HomeStackNavigator} 
        options={{
          tabBarLabel: 'Ana Sayfa',
          tabBarIcon: ({ color, size }) => (
            <Icon name="home" color={color} size={size} />
          ),
        }}
        listeners={({ navigation }) => ({
          tabPress: e => {
            // Tab'a her tıklandığında doğrudan ana sayfa ekranına git
            e.preventDefault();
            navigation.navigate('Home', {
              screen: 'HomeMain'
            });
          },
        })}
      />
      <Tab.Screen 
        name="Children" 
        component={ChildrenStackNavigator}
        options={{
          tabBarLabel: 'Çocuklarım',
          tabBarIcon: ({ color, size }) => (
            <Icon name="account-child" color={color} size={size} />
          ),
        }}
        listeners={({ navigation }) => ({
          tabPress: e => {
            // Tab'a her tıklandığında doğrudan çocuklar ana sayfasına git
            e.preventDefault();
            navigation.navigate('Children', {
              screen: 'ChildrenList'
            });
          },
        })}
      />
      <Tab.Screen 
        name="Activities" 
        component={ActivitiesStackNavigator}
        options={{
          tabBarLabel: 'Ziyaretler',
          tabBarIcon: ({ color, size }) => (
            <Icon name="map-marker-check" color={color} size={size} />
          ),
        }}
        listeners={({ navigation }) => ({
          tabPress: e => {
            // Tab'a her tıklandığında doğrudan ziyaretler ana sayfasına git
            e.preventDefault();
            navigation.navigate('Activities', {
              screen: 'ActivitiesList'
            });
          },
        })}
      />
      <Tab.Screen 
        name="Menu" 
        component={MenuStackNavigator}
        options={{
          tabBarLabel: 'Menü',
          tabBarIcon: ({ color, size }) => (
            <Icon name="food" color={color} size={size} />
          ),
        }}
        listeners={({ navigation }) => ({
          tabPress: e => {
            // Tab'a her tıklandığında doğrudan menü ana sayfasına git
            e.preventDefault();
            navigation.navigate('Menu', {
              screen: 'MenuMain'
            });
          },
        })}
      />
      <Tab.Screen 
        name="Profile" 
        component={ProfileStackNavigator}
        options={{
          tabBarLabel: 'Profil',
          tabBarIcon: ({ color, size }) => (
            <Icon name="account" color={color} size={size} />
          ),
        }}
        listeners={({ navigation }) => ({
          tabPress: e => {
            // Tab'a her tıklandığında doğrudan profil ana sayfasına git
            e.preventDefault();
            navigation.navigate('Profile', {
              screen: 'ProfileMain'
            });
          },
        })}
      />
    </Tab.Navigator>
  );
};

export default UserNavigator;
